import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tab,
  Link as MuiLink,
  Chip,
  Button,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import ShieldIcon from "@mui/icons-material/Shield";
import LinkIcon from "@mui/icons-material/Link";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import VendorRiskBadge from "../components/VendorRiskBadge";
import StatusChip from "../components/StatusChip";
import VendorOwnerSelect from "../components/VendorOwnerSelect";
import useVendorStore from "../store/useVendorStore";
import useVendorStaff, { getOwnerLabel } from "../hooks/useVendorStaff";
import OverviewTab from "./tabs/OverviewTab";
import AssessmentTab from "./tabs/AssessmentTab";
import ActivityTab from "./tabs/ActivityTab";
import { styles } from "../styles/Vendors.styles";
import { getDisplayedReviewStatus } from "../utils/constants";

const MetaItem = ({ icon, label, children }) => {
  const theme = useTheme();
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="caption" color={theme.customColors.text.secondary}>
          {label}
        </Typography>
      </Box>
      <Box>{children || "-"}</Box>
    </Grid>
  );
};

const VendorDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { vendorId } = useParams();
  const vendor = useVendorStore((s) => s.vendors.find((v) => v.id === vendorId));
  const updateVendorOwner = useVendorStore((s) => s.updateVendorOwner);
  const assignOwnersFromStaff = useVendorStore((s) => s.assignOwnersFromStaff);
  const { people } = useVendorStaff();
  const [tab, setTab] = useState("0");
  const [editingOwner, setEditingOwner] = useState(false);
  const [tempOwner, setTempOwner] = useState("");

  useEffect(() => {
    if (people.length) assignOwnersFromStaff(people);
  }, [people, assignOwnersFromStaff]);

  useEffect(() => {
    setEditingOwner(false);
    setTempOwner("");
  }, [vendorId]);

  if (!vendor) {
    return (
      <Box sx={styles.pageContainer}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/vendors")}
          sx={{
            mb: 2,
            color: theme.customColors.text.secondary,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Back to Overview
        </Button>
        <Typography variant="h5" color="error">
          Vendor not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/vendors")}
          sx={{
            color: theme.customColors.text.secondary,
            textTransform: "none",
            fontWeight: 500,
            "&:hover": { bgcolor: theme.customColors.background.light },
          }}
        >
          Back to Overview
        </Button>
      </Box>

      <Card sx={{ ...styles.mainCard, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Typography variant="h4" fontWeight="600" color={theme.customColors.text.strong}>
              {vendor.name}
            </Typography>
            <VendorRiskBadge level={vendor.inherentRisk} />
            <StatusChip label={getDisplayedReviewStatus(vendor)} />
            {vendor.status === "Archived" && (
              <Chip
                size="small"
                label="Archived"
                sx={{
                  bgcolor: theme.customColors.background.light,
                  color: theme.customColors.text.secondary,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>

          {vendor.services && (
            <Typography variant="body2" sx={{ mb: 3 }}>
              {vendor.services}
            </Typography>
          )}

          <Grid container spacing={3}>
            <MetaItem
              icon={<PersonIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Owner"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {editingOwner ? (
                  <>
                    <Box sx={{ minWidth: 220, flex: 1 }}>
                      <VendorOwnerSelect
                        value={tempOwner}
                        onChange={setTempOwner}
                        people={people}
                        label="Owner"
                      />
                    </Box>
                    <Tooltip title="Save">
                      <IconButton
                        size="small"
                        onClick={() => {
                          updateVendorOwner(vendor.id, tempOwner);
                          setEditingOwner(false);
                        }}
                        sx={{
                          color: theme.customColors.button.success.main,
                          "&:hover": { bgcolor: theme.customColors.button.success.light },
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={() => setEditingOwner(false)}
                        sx={{
                          color: theme.customColors.text.secondary,
                          "&:hover": { bgcolor: theme.customColors.background.light },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" fontWeight="600">
                      {getOwnerLabel(people, vendor.owner)}
                    </Typography>
                    {vendor.status !== "Archived" && (
                      <Tooltip title="Edit owner">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTempOwner(vendor.owner || "");
                            setEditingOwner(true);
                          }}
                          sx={{
                            p: 0.5,
                            ml: 0.5,
                            color: theme.customColors.text.secondary,
                            "&:hover": { bgcolor: theme.customColors.background.light },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              </Box>
            </MetaItem>
            <MetaItem
              icon={<CategoryIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Category"
            >
              <Typography variant="body2" fontWeight="600">
                {vendor.category || "-"}
              </Typography>
            </MetaItem>
            <MetaItem
              icon={<BusinessIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Source"
            >
              <Typography variant="body2" fontWeight="600">
                {vendor.source === "Discovered"
                  ? `Discovered (${vendor.discoveredFrom})`
                  : "Manual"}
              </Typography>
            </MetaItem>
            <MetaItem
              icon={<ShieldIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Residual Risk"
            >
              <VendorRiskBadge level={vendor.residualRisk} />
            </MetaItem>
            <MetaItem
              icon={<ScheduleIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Contract"
            >
              <Typography variant="body2" fontWeight="600">
                {vendor.contractStart || vendor.contractEnd
                  ? `${vendor.contractStart || "?"} – ${vendor.contractEnd || "?"}`
                  : "-"}
              </Typography>
            </MetaItem>
            <MetaItem
              icon={<PersonIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Contact"
            >
              {vendor.contactEmail ? (
                <MuiLink href={`mailto:${vendor.contactEmail}`} sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                  {vendor.contactName || vendor.contactEmail}
                </MuiLink>
              ) : (
                <Typography variant="body2" fontWeight="600">
                  {vendor.contactName || "-"}
                </Typography>
              )}
            </MetaItem>
            <MetaItem
              icon={<LinkIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Website"
            >
              {vendor.website ? (
                <MuiLink href={vendor.website} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                  {vendor.website.replace(/^https?:\/\//, "")}
                </MuiLink>
              ) : (
                <Typography variant="body2" fontWeight="600">-</Typography>
              )}
            </MetaItem>
            <MetaItem
              icon={<LinkIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />}
              label="Trust Center"
            >
              {vendor.trustCenterUrl ? (
                <MuiLink href={vendor.trustCenterUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                  View
                </MuiLink>
              ) : (
                <Typography variant="body2" fontWeight="600">-</Typography>
              )}
            </MetaItem>
          </Grid>
        </CardContent>
      </Card>

      <TabContext value={tab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: theme.customColors.border.main,
            mb: 2,
          }}
        >
          <TabList onChange={(_, v) => setTab(v)} aria-label="Vendor detail tabs" variant="scrollable" scrollButtons="auto">
            <Tab label="Overview" value="0" />
            <Tab label="Security Review" value="1" />
            <Tab label="Activity" value="2" />
          </TabList>
        </Box>

        <TabPanel value="0" sx={{ p: 0 }}>
          <OverviewTab vendor={vendor} onOpenReview={() => setTab("1")} />
        </TabPanel>
        <TabPanel value="1" sx={{ p: 0 }}>
          <AssessmentTab vendor={vendor} people={people} />
        </TabPanel>
        <TabPanel value="2" sx={{ p: 0 }}>
          <ActivityTab vendor={vendor} people={people} />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default VendorDetails;
